// =============================================================================
// MEMORY CONTROLS
// =============================================================================
// Large, accessible controls for photo navigation - dementia-friendly

import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Play, Pause, X } from '../ui/Icons.js';

interface MemoryControlsProps {
  currentIndex: number;
  totalPhotos: number;
  isAutoPlaying: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onToggleAutoPlay: () => void;
  onEndSession: () => void;
  controlSize: 'small' | 'medium' | 'large';
}

export function MemoryControls({
  currentIndex,
  totalPhotos,
  isAutoPlaying,
  onPrevious,
  onNext,
  onToggleAutoPlay,
  onEndSession,
  controlSize,
}: MemoryControlsProps) {
  const { t } = useTranslation();

  // Size classes based on controlSize
  const sizeClasses = {
    small: {
      button: 'h-10 w-10 text-lg',
      mainButton: 'h-12 w-12 text-xl',
      text: 'text-sm',
    },
    medium: {
      button: 'h-12 w-12 text-xl',
      mainButton: 'h-14 w-14 text-2xl',
      text: 'text-base',
    },
    large: {
      button: 'h-16 w-16 text-2xl',
      mainButton: 'h-20 w-20 text-3xl',
      text: 'text-lg',
    },
  };

  const sizes = sizeClasses[controlSize];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalPhotos - 1;

  return (
    <div className="flex items-center justify-between bg-bogwood-900 px-6 py-4 safe-area-pb">
      {/* Progress indicator */}
      <div className={`min-w-[80px] ${sizes.text} text-linen-200`}>
        <span className="font-bold text-white">{currentIndex + 1}</span>
        <span className="mx-1">/</span>
        <span>{totalPhotos}</span>
      </div>

      {/* Navigation controls */}
      <div className="flex items-center gap-4">
        {/* Previous button */}
        <button
          onClick={onPrevious}
          disabled={isFirst}
          className={`flex items-center justify-center rounded-full transition-all ${sizes.button} ${
            isFirst
              ? 'bg-bogwood-700 text-linen-300 cursor-not-allowed'
              : 'bg-bogwood-600 text-white hover:bg-bogwood-500 active:scale-95'
          }`}
          aria-label={t('memory.previous', 'Previous photo')}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        {/* Play/Pause button */}
        <button
          onClick={onToggleAutoPlay}
          className={`flex items-center justify-center rounded-full bg-primary text-white transition-all hover:bg-primary active:scale-95 ${sizes.mainButton}`}
          aria-label={
            isAutoPlaying
              ? t('memory.pause', 'Pause slideshow')
              : t('memory.play', 'Play slideshow')
          }
        >
          {isAutoPlaying ? (
            <Pause className="h-8 w-8" />
          ) : (
            <Play className="h-8 w-8 ml-1" />
          )}
        </button>

        {/* Next button */}
        <button
          onClick={onNext}
          disabled={isLast}
          className={`flex items-center justify-center rounded-full transition-all ${sizes.button} ${
            isLast
              ? 'bg-bogwood-700 text-linen-300 cursor-not-allowed'
              : 'bg-bogwood-600 text-white hover:bg-bogwood-500 active:scale-95'
          }`}
          aria-label={t('memory.next', 'Next photo')}
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* End session button */}
      <button
        onClick={onEndSession}
        className={`flex items-center justify-center rounded-full bg-danger text-white transition-all hover:bg-danger/80 active:scale-95 ${sizes.button}`}
        aria-label={t('memory.endSession', 'End memory session')}
      >
        <X className="h-6 w-6" />
      </button>
    </div>
  );
}
